import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { computeNextRunDate } from "@/lib/recurring-agreements-calc";
import type {
  DBRecurringAgreement,
  RecurringAgreementLine,
  RecurringAgreementInterval,
} from "@/lib/db-types";

// recurring_agreements er godt under PostgREST sin 1000-rads-grense
// (scripts/paginate-gate.mjs sin BIG_TABLES-liste) — plain select uten
// paginering er trygt her.
export interface RecurringAgreementWithCustomer extends DBRecurringAgreement {
  customer?: { id: string; name: string } | null;
  project?: { id: string; name: string; project_code: string | null } | null;
}

const SELECT_WITH_RELATIONS =
  "*, customer:customers(id, name), project:projects(id, name, project_code)";

/** Global liste — brukes av /avtaler-oversikten. */
export function useRecurringAgreements() {
  return useQuery({
    queryKey: ["recurring-agreements"],
    queryFn: async (): Promise<RecurringAgreementWithCustomer[]> => {
      const { data, error } = await supabase
        .from("recurring_agreements")
        .select(SELECT_WITH_RELATIONS)
        .is("deleted_at", null)
        .order("next_run_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as RecurringAgreementWithCustomer[];
    },
    staleTime: 60 * 1000,
  });
}

/** Per-kunde liste — brukes av kundekortet (VIB-1290). */
export function useRecurringAgreementsByCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: ["recurring-agreements", { customerId }],
    queryFn: async (): Promise<RecurringAgreementWithCustomer[]> => {
      if (!customerId) return [];
      const { data, error } = await supabase
        .from("recurring_agreements")
        .select(SELECT_WITH_RELATIONS)
        .eq("customer_id", customerId)
        .is("deleted_at", null)
        .order("next_run_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as RecurringAgreementWithCustomer[];
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });
}

export interface CreateAgreementInput {
  customer_id: string;
  project_id?: string | null;
  type: "recurring" | "rental";
  description?: string | null;
  rental_object?: string | null;
  lines: RecurringAgreementLine[];
  billing_interval: "monthly" | "quarterly" | "yearly";
  start_date: string;
  end_date?: string | null;
  next_run_date: string;
  auto_send?: boolean;
}

export interface UpdateAgreementInput {
  id: string;
  customer_id?: string;
  description?: string | null;
  rental_object?: string | null;
  lines?: RecurringAgreementLine[];
  billing_interval?: "monthly" | "quarterly" | "yearly";
  start_date?: string;
  end_date?: string | null;
  next_run_date?: string;
  auto_send?: boolean;
}

/**
 * Mutations for løpende avtaler. Audit-dekning skjer DB-side (trigger
 * log_recurring_agreement_change/deleted i migrasjonen) — hooken trenger
 * ingen egen logEvent-kall, kun å sette created_by til den FAKTISK
 * innloggede brukeren (useAuth, ALDRI impersonert bruker) ved opprettelse,
 * slik at COALESCE(current_user_id(), created_by) alltid har en ekte aktør.
 */
export function useRecurringAgreementMutations() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();

  const invalidate = async (customerId?: string) => {
    await invalidateFor(qc, "recurring_agreements", { customerId });
  };

  const createAgreement = useMutation({
    mutationFn: async (input: CreateAgreementInput): Promise<DBRecurringAgreement> => {
      const { data, error } = await supabase
        .from("recurring_agreements")
        .insert({
          customer_id: input.customer_id,
          project_id: input.project_id ?? null,
          type: input.type,
          description: input.description ?? null,
          rental_object: input.rental_object ?? null,
          lines: input.lines,
          billing_interval: input.billing_interval,
          start_date: input.start_date,
          end_date: input.end_date ?? null,
          next_run_date: input.next_run_date,
          auto_send: input.auto_send ?? false,
          created_by: authUser?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBRecurringAgreement;
    },
    onSuccess: async (row) => {
      await invalidate(row.customer_id);
      toast.success("Avtale opprettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke opprette avtale: ${err.message}`),
  });

  const updateAgreement = useMutation({
    mutationFn: async (input: UpdateAgreementInput): Promise<DBRecurringAgreement> => {
      const { id, ...patch } = input;
      const { data, error } = await supabase
        .from("recurring_agreements")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as DBRecurringAgreement;
    },
    onSuccess: async (row) => {
      await invalidate(row.customer_id);
      toast.success("Avtale oppdatert");
    },
    onError: (err: Error) => toast.error(`Kunne ikke oppdatere avtale: ${err.message}`),
  });

  const setAgreementStatus = useMutation({
    mutationFn: async (input: {
      id: string;
      status: "active" | "paused" | "ended";
    }): Promise<DBRecurringAgreement> => {
      const { data, error } = await supabase
        .from("recurring_agreements")
        .update({ status: input.status })
        .eq("id", input.id)
        .select()
        .single();
      if (error) throw error;
      return data as DBRecurringAgreement;
    },
    onSuccess: async (row) => {
      await invalidate(row.customer_id);
      const label =
        row.status === "paused" ? "satt på pause" : row.status === "ended" ? "avsluttet" : "gjenopptatt";
      toast.success(`Avtale ${label}`);
    },
    onError: (err: Error) => toast.error(`Kunne ikke endre avtalestatus: ${err.message}`),
  });

  /**
   * VIB-1308: opprett en storage-avtale automatisk ved prosjektlukking.
   * 12 mnd lagring er inkludert fra lukkedato — included_until = start_date +
   * 12 mnd (computeNextRunDate med 'yearly'), og first-run = included_until
   * (fakturering starter når den inkluderte perioden er ute). Selve
   * beløpet beregnes av storage-pricing.ts + tiers når motoren kjører.
   *
   * billing_interval defaulter til 'monthly' (avklaring B3 blokkert) — kan
   * overstyres når Sølvrev har bekreftet intervallet.
   */
  const createStorageAgreement = useMutation({
    mutationFn: async (input: {
      customer_id: string;
      project_id: string;
      storage_gb: number;
      storage_location?: string | null;
      start_date: string;
      billing_interval?: RecurringAgreementInterval;
    }): Promise<DBRecurringAgreement> => {
      const interval = input.billing_interval ?? "monthly";
      const includedUntil = computeNextRunDate(input.start_date, "yearly");
      const { data, error } = await supabase
        .from("recurring_agreements")
        .insert({
          customer_id: input.customer_id,
          project_id: input.project_id,
          type: "storage",
          lines: [],
          billing_interval: interval,
          start_date: input.start_date,
          next_run_date: includedUntil,
          included_until: includedUntil,
          storage_gb: input.storage_gb,
          storage_location: input.storage_location ?? null,
          created_by: authUser?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBRecurringAgreement;
    },
    onSuccess: async (row) => {
      await invalidate(row.customer_id);
    },
    onError: (err: Error) => toast.error(`Kunne ikke opprette lagringsavtale: ${err.message}`),
  });

  /** Soft-delete: deleted_at + status='ended'. Hard delete er forbudt (RLS har ingen DELETE-policy uansett). */
  const softDeleteAgreement = useMutation({
    mutationFn: async (input: { id: string; customer_id: string }): Promise<void> => {
      const { error } = await supabase
        .from("recurring_agreements")
        .update({ deleted_at: new Date().toISOString(), status: "ended" })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidate(vars.customer_id);
      toast.success("Avtale slettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke slette avtale: ${err.message}`),
  });

  return {
    createAgreement,
    updateAgreement,
    setAgreementStatus,
    softDeleteAgreement,
    createStorageAgreement,
  };
}

export interface RunRecurringInvoicesResult {
  success: boolean;
  due: number;
  created: number;
  resumed: number;
  skipped: number;
  failed: number;
  storage_skipped: number;
}

/**
 * VIB-1292: manuell «kjør nå» av fakturamotoren (run-recurring-invoices).
 * Kaller edge-funksjonen med brukerens JWT — edge verifiserer admin/superadmin
 * og bruker caller som aktør. Primært for staging-verifisering (VIB-1293).
 * Auto-send styres av system_config-bryteren; motoren lager UTKAST når av.
 */
export function useRunRecurringInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RunRecurringInvoicesResult> => {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/run-recurring-invoices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Fakturamotoren feilet");
      return data as RunRecurringInvoicesResult;
    },
    onSuccess: async (data) => {
      await invalidateFor(qc, "recurring_agreements");
      await invalidateFor(qc, "invoices");
      toast.success(
        `Fakturamotor kjørt: ${data.created} opprettet, ${data.skipped} hoppet over, ${data.failed} feilet`,
      );
    },
    onError: (err: Error) => toast.error(`Kunne ikke kjøre fakturamotoren: ${err.message}`),
  });
}
